import React, { useState, useEffect } from 'react';

interface RevenueStream {
  id: string;
  name: string;
  currentRevenue: number;
  projectedRevenue: number;
  growthPotential: number;
  optimization: 'low' | 'medium' | 'high' | 'critical';
  category: 'subscriptions' | 'premium_placement' | 'trusted_testing' | 'lab_verification' | 'advertising' | 'data_insights';
  metrics: {
    conversionRate: number;
    averageValue: number;
    churnRate?: number;
    seasonality: number;
  };
}

interface OptimizationOpportunity {
  id: string;
  title: string;
  description: string;
  category: string;
  impact: 'high' | 'medium' | 'low';
  effort: 'low' | 'medium' | 'high';
  estimatedRevenue: number;
  timeToImplement: string;
  priority: number;
  steps: string[];
  kpis: string[];
  risks: string[];
}

interface PricingStrategy {
  id: string;
  service: string;
  currentPricing: {
    basic: number;
    professional: number;
    enterprise: number;
  };
  optimizedPricing: {
    basic: number;
    professional: number;
    enterprise: number;
  };
  elasticity: number;
  demandForecast: Array<{
    tier: string;
    currentUsers: number;
    projectedUsers: number;
    revenueImpact: number;
  }>;
}

interface RevenueOptimizationData {
  overview: {
    totalRevenue: number;
    optimizationPotential: number;
    quickWins: number;
    longTermGains: number;
  };
  revenueStreams: RevenueStream[];
  opportunities: OptimizationOpportunity[];
  pricingStrategies: PricingStrategy[];
  forecasting: {
    monthly: Array<{
      month: string;
      current: number;
      optimized: number;
      growth: number;
    }>;
    scenarios: Array<{
      name: string;
      description: string;
      probability: number;
      revenueImpact: number;
      timeframe: string;
    }>;
  };
  actionPlan: Array<{
    phase: string;
    duration: string;
    initiatives: string[];
    expectedRevenue: number;
    resources: string[];
  }>;
}

interface RevenueOptimizationDashboardProps {
  userRole: 'admin' | 'staff';
  timeframe: '30d' | '90d' | '1y';
}

export default function RevenueOptimizationDashboard({ userRole, timeframe = '90d' }: RevenueOptimizationDashboardProps) {
  const [optimizationData, setOptimizationData] = useState<RevenueOptimizationData | null>(null);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'streams' | 'opportunities' | 'pricing' | 'forecast' | 'action'>('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOptimizationData = async () => {
      setLoading(true);
      
      setTimeout(() => {
        const mockRevenueStreams: RevenueStream[] = [
          {
            id: '1',
            name: 'Vendor Subscriptions',
            currentRevenue: 127450,
            projectedRevenue: 198750,
            growthPotential: 56.0,
            optimization: 'high',
            category: 'subscriptions',
            metrics: {
              conversionRate: 12.4,
              averageValue: 79,
              churnRate: 8.2,
              seasonality: 15.3
            }
          },
          {
            id: '2',
            name: 'Premium Placement',
            currentRevenue: 34200,
            projectedRevenue: 67800,
            growthPotential: 98.2,
            optimization: 'critical',
            category: 'premium_placement',
            metrics: {
              conversionRate: 18.7,
              averageValue: 150,
              seasonality: 22.1
            }
          },
          {
            id: '3',
            name: 'Trusted Tester Program',
            currentRevenue: 18900,
            projectedRevenue: 45600,
            growthPotential: 141.3,
            optimization: 'critical',
            category: 'trusted_testing',
            metrics: {
              conversionRate: 34.5,
              averageValue: 25,
              seasonality: 8.7
            }
          },
          {
            id: '4',
            name: 'Lab Verification Services',
            currentRevenue: 12600,
            projectedRevenue: 28900,
            growthPotential: 129.4,
            optimization: 'high',
            category: 'lab_verification',
            metrics: {
              conversionRate: 45.2,
              averageValue: 75,
              seasonality: 12.4
            }
          },
          {
            id: '5',
            name: 'Data Insights API',
            currentRevenue: 0,
            projectedRevenue: 15400,
            growthPotential: 0,
            optimization: 'critical',
            category: 'data_insights',
            metrics: {
              conversionRate: 0,
              averageValue: 200,
              seasonality: 5.2
            }
          }
        ];

        const mockOpportunities: OptimizationOpportunity[] = [
          {
            id: '1',
            title: 'Tiered Premium Placement',
            description: 'Introduce multiple tiers of premium placement with different visibility levels and pricing.',
            category: 'Premium Placement',
            impact: 'high',
            effort: 'medium',
            estimatedRevenue: 28400,
            timeToImplement: '6-8 weeks',
            priority: 1,
            steps: [
              'Design 3-tier premium placement system',
              'Implement algorithmic placement logic',
              'Create vendor dashboard controls',
              'Launch with A/B testing'
            ],
            kpis: ['Premium placement conversion rate', 'Average revenue per placement', 'Vendor satisfaction'],
            risks: ['Vendor confusion', 'Technical complexity', 'Search result quality impact']
          },
          {
            id: '2',
            title: 'Subscription Tier Optimization',
            description: 'Restructure subscription tiers based on usage data and add premium features.',
            category: 'Subscriptions',
            impact: 'high',
            effort: 'low',
            estimatedRevenue: 45200,
            timeToImplement: '3-4 weeks',
            priority: 2,
            steps: [
              'Analyze current tier usage patterns',
              'Design optimized tier structure',
              'Implement grandfathering for existing users',
              'Launch new pricing with migration path'
            ],
            kpis: ['Average revenue per user', 'Tier upgrade rate', 'Churn rate'],
            risks: ['User backlash', 'Competitor advantage', 'Implementation complexity']
          },
          {
            id: '3',
            title: 'Data Insights Monetization',
            description: 'Launch API service for kratom market data and trends for researchers and businesses.',
            category: 'New Revenue Stream',
            impact: 'medium',
            effort: 'high',
            estimatedRevenue: 15400,
            timeToImplement: '12-16 weeks',
            priority: 3,
            steps: [
              'Develop data API infrastructure',
              'Create tiered access plans',
              'Build developer documentation',
              'Establish enterprise partnerships'
            ],
            kpis: ['API usage growth', 'Enterprise contract value', 'Data quality metrics'],
            risks: ['Data privacy concerns', 'Technical scalability', 'Market demand uncertainty']
          },
          {
            id: '4',
            title: 'Trusted Tester Expansion',
            description: 'Scale trusted tester program with automated matching and premium tester tiers.',
            category: 'Trusted Testing',
            impact: 'high',
            effort: 'medium',
            estimatedRevenue: 22100,
            timeToImplement: '8-10 weeks',
            priority: 4,
            steps: [
              'Implement AI-powered tester matching',
              'Create premium tester certification',
              'Build automated workflow system',
              'Launch vendor bulk testing packages'
            ],
            kpis: ['Tester utilization rate', 'Review quality scores', 'Vendor retention'],
            risks: ['Quality control', 'Tester supply constraints', 'Operational complexity']
          }
        ];

        const mockPricingStrategies: PricingStrategy[] = [
          {
            id: '1',
            service: 'Vendor Subscriptions',
            currentPricing: {
              basic: 29,
              professional: 79,
              enterprise: 149
            },
            optimizedPricing: {
              basic: 39,
              professional: 99,
              enterprise: 199
            },
            elasticity: -0.7,
            demandForecast: [
              { tier: 'Basic', currentUsers: 89, projectedUsers: 76, revenueImpact: 2640 },
              { tier: 'Professional', currentUsers: 45, projectedUsers: 42, revenueImpact: 2520 },
              { tier: 'Enterprise', currentUsers: 22, projectedUsers: 21, revenueImpact: 1050 }
            ]
          },
          {
            id: '2',
            service: 'Premium Placement',
            currentPricing: {
              basic: 50,
              professional: 150,
              enterprise: 300
            },
            optimizedPricing: {
              basic: 75,
              professional: 200,
              enterprise: 400
            },
            elasticity: -0.4,
            demandForecast: [
              { tier: 'Basic', currentUsers: 34, projectedUsers: 31, revenueImpact: 775 },
              { tier: 'Professional', currentUsers: 18, projectedUsers: 17, revenueImpact: 850 },
              { tier: 'Enterprise', currentUsers: 8, projectedUsers: 8, revenueImpact: 800 }
            ]
          }
        ];

        const mockData: RevenueOptimizationData = {
          overview: {
            totalRevenue: 193150,
            optimizationPotential: 67890,
            quickWins: 23400,
            longTermGains: 44490
          },
          revenueStreams: mockRevenueStreams,
          opportunities: mockOpportunities,
          pricingStrategies: mockPricingStrategies,
          forecasting: {
            monthly: [
              { month: 'Feb 2024', current: 193150, optimized: 221850, growth: 14.8 },
              { month: 'Mar 2024', current: 198200, optimized: 235600, growth: 18.9 },
              { month: 'Apr 2024', current: 203400, optimized: 248900, growth: 22.4 },
              { month: 'May 2024', current: 208800, optimized: 262800, growth: 25.9 },
              { month: 'Jun 2024', current: 214300, optimized: 277200, growth: 29.4 }
            ],
            scenarios: [
              {
                name: 'Conservative Growth',
                description: 'Implement only high-confidence optimizations',
                probability: 85,
                revenueImpact: 45200,
                timeframe: '6 months'
              },
              {
                name: 'Aggressive Expansion',
                description: 'Launch all optimization initiatives simultaneously',
                probability: 60,
                revenueImpact: 89600,
                timeframe: '8 months'
              },
              {
                name: 'Market Disruption',
                description: 'Major competitor enters market or regulation changes',
                probability: 25,
                revenueImpact: -34500,
                timeframe: '12 months'
              }
            ]
          },
          actionPlan: [
            {
              phase: 'Phase 1: Quick Wins',
              duration: '4-6 weeks',
              initiatives: ['Subscription tier optimization', 'Basic premium placement tiers'],
              expectedRevenue: 23400,
              resources: ['Product team', 'Engineering', 'Marketing']
            },
            {
              phase: 'Phase 2: Core Improvements',
              duration: '8-12 weeks',
              initiatives: ['Advanced premium placement', 'Trusted tester expansion'],
              expectedRevenue: 35700,
              resources: ['Full engineering team', 'Operations', 'Customer success']
            },
            {
              phase: 'Phase 3: Innovation',
              duration: '16-20 weeks',
              initiatives: ['Data insights API', 'Advanced analytics', 'Enterprise partnerships'],
              expectedRevenue: 28900,
              resources: ['Data team', 'Business development', 'Enterprise sales']
            }
          ]
        };

        setOptimizationData(mockData);
        setLoading(false);
      }, 1500);
    };

    fetchOptimizationData();
  }, [timeframe]);

  const getOptimizationColor = (level: string) => {
    switch (level) {
      case 'critical': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Optimizing revenue strategies...</p>
      </div>
    );
  }

  if (!optimizationData) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Optimization data not available</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#e2e8f0' }}>
          Revenue Optimization Dashboard
        </h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>
          Strategic insights and actionable recommendations to maximize platform revenue
        </p>
      </div>

      {/* Key Metrics Overview */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1.5rem', 
        marginBottom: '3rem' 
      }}>
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '2rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          border: '1px solid #4a5568',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.5rem' }}>
            ${optimizationData.overview.totalRevenue.toLocaleString()}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Current Monthly Revenue</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '2rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          border: '1px solid #4a5568',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.5rem' }}>
            ${optimizationData.overview.optimizationPotential.toLocaleString()}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Optimization Potential (+{((optimizationData.overview.optimizationPotential / optimizationData.overview.totalRevenue) * 100).toFixed(1)}%)
          </div>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '2rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          border: '1px solid #4a5568',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#f59e0b', marginBottom: '0.5rem' }}>
            ${optimizationData.overview.quickWins.toLocaleString()}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Quick Wins (0-3 months)</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '2rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          border: '1px solid #4a5568',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#8b5cf6', marginBottom: '0.5rem' }}>
            ${optimizationData.overview.longTermGains.toLocaleString()}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Long-term Gains (3-12 months)</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ borderBottom: '2px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '2rem', overflowX: 'auto' }}>
            {(['overview', 'streams', 'opportunities', 'pricing', 'forecast', 'action'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                style={{
                  padding: '1rem 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: selectedTab === tab ? '3px solid #3b82f6' : '3px solid transparent',
                  color: selectedTab === tab ? '#3b82f6' : '#6b7280',
                  fontSize: '1rem',
                  fontWeight: selectedTab === tab ? '600' : '500',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease'
                }}
              >
                {tab === 'streams' ? 'Revenue Streams' : 
                 tab === 'forecast' ? 'Forecasting' :
                 tab === 'action' ? 'Action Plan' : tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {selectedTab === 'streams' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {optimizationData.revenueStreams.map(stream => (
            <div key={stream.id} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '1px solid #4a5568'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div style={{ flex: '1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {stream.name}
                    </h3>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.75rem',
                      backgroundColor: getOptimizationColor(stream.optimization) + '20',
                      color: getOptimizationColor(stream.optimization),
                      borderRadius: '0.25rem',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {stream.optimization} Priority
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize' }}>
                    {stream.category.replace('_', ' ')}
                  </div>
                </div>
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.25rem' }}>
                    +{stream.growthPotential.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Growth Potential</div>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                    ${stream.currentRevenue.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Current Revenue</div>
                </div>
                
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
                    ${stream.projectedRevenue.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Projected Revenue</div>
                </div>
                
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f59e0b' }}>
                    {stream.metrics.conversionRate.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Conversion Rate</div>
                </div>
                
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                    ${stream.metrics.averageValue}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Average Value</div>
                </div>
              </div>

              {stream.metrics.churnRate && (
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: stream.metrics.churnRate > 10 ? '#fef2f2' : '#f0fdf4', 
                  borderRadius: '0.5rem',
                  border: `1px solid ${stream.metrics.churnRate > 10 ? '#fecaca' : '#bbf7d0'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: stream.metrics.churnRate > 10 ? '#dc2626' : '#166534' }}>
                      Monthly Churn Rate: {stream.metrics.churnRate.toFixed(1)}%
                    </span>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      Seasonality: ±{stream.metrics.seasonality.toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedTab === 'opportunities' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {optimizationData.opportunities.sort((a, b) => a.priority - b.priority).map(opportunity => (
            <div key={opportunity.id} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '1px solid #4a5568'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div style={{ flex: '1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%', 
                      backgroundColor: '#3b82f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 'bold'
                    }}>
                      #{opportunity.priority}
                    </div>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {opportunity.title}
                    </h3>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: getImpactColor(opportunity.impact) + '20',
                      color: getImpactColor(opportunity.impact),
                      borderRadius: '0.25rem',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {opportunity.impact} Impact
                    </span>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: getEffortColor(opportunity.effort) + '20',
                      color: getEffortColor(opportunity.effort),
                      borderRadius: '0.25rem',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {opportunity.effort} Effort
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    {opportunity.category} • {opportunity.timeToImplement}
                  </div>
                  <p style={{ fontSize: '1rem', color: '#a0aec0', margin: '0', lineHeight: '1.6' }}>
                    {opportunity.description}
                  </p>
                </div>
                
                <div style={{ textAlign: 'right', minWidth: '150px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.25rem' }}>
                    ${opportunity.estimatedRevenue.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Estimated Revenue</div>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '1.5rem' 
              }}>
                {/* Implementation Steps */}
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                    Implementation Steps
                  </h4>
                  <ol style={{ margin: '0', paddingLeft: '1.5rem', color: '#a0aec0' }}>
                    {opportunity.steps.map((step, index) => (
                      <li key={index} style={{ marginBottom: '0.5rem', fontSize: '0.875rem', lineHeight: '1.5' }}>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* KPIs */}
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                    Success Metrics
                  </h4>
                  <ul style={{ margin: '0', paddingLeft: '1.5rem', color: '#a0aec0' }}>
                    {opportunity.kpis.map((kpi, index) => (
                      <li key={index} style={{ marginBottom: '0.5rem', fontSize: '0.875rem', lineHeight: '1.5' }}>
                        {kpi}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Risks */}
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                    Key Risks
                  </h4>
                  <ul style={{ margin: '0', paddingLeft: '1.5rem', color: '#a0aec0' }}>
                    {opportunity.risks.map((risk, index) => (
                      <li key={index} style={{ marginBottom: '0.5rem', fontSize: '0.875rem', lineHeight: '1.5' }}>
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedTab === 'pricing' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {optimizationData.pricingStrategies.map(strategy => (
            <div key={strategy.id} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '1px solid #4a5568'
            }}>
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                  {strategy.service} - Pricing Optimization
                </h3>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Price Elasticity: {strategy.elasticity} (demand decreases {Math.abs(strategy.elasticity * 100)}% per 10% price increase)
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
                {/* Current vs Optimized Pricing */}
                <div>
                  <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '1rem' }}>
                    Pricing Comparison
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {Object.entries(strategy.currentPricing).map(([tier, price]) => (
                      <div key={tier} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '0.75rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '0.375rem'
                      }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#e2e8f0', textTransform: 'capitalize' }}>
                          {tier}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            ${price} → ${strategy.optimizedPricing[tier as keyof typeof strategy.optimizedPricing]}
                          </span>
                          <span style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: '600',
                            color: strategy.optimizedPricing[tier as keyof typeof strategy.optimizedPricing] > price ? '#10b981' : '#ef4444'
                          }}>
                            {strategy.optimizedPricing[tier as keyof typeof strategy.optimizedPricing] > price ? '+' : ''}
                            {(((strategy.optimizedPricing[tier as keyof typeof strategy.optimizedPricing] - price) / price) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Demand Forecast */}
                <div>
                  <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '1rem' }}>
                    Demand Impact Forecast
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {strategy.demandForecast.map(forecast => (
                      <div key={forecast.tier} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '0.75rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '0.375rem'
                      }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#e2e8f0' }}>
                          {forecast.tier}
                        </span>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            {forecast.currentUsers} → {forecast.projectedUsers} users
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: '600',
                            color: forecast.revenueImpact > 0 ? '#10b981' : '#ef4444'
                          }}>
                            {forecast.revenueImpact > 0 ? '+' : ''}${forecast.revenueImpact.toLocaleString()} revenue
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#eff6ff', 
                borderRadius: '0.5rem',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>
                  Net Revenue Impact
                </div>
                <div style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1e40af' }}>
                  +${strategy.demandForecast.reduce((total, forecast) => total + forecast.revenueImpact, 0).toLocaleString()} per month
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedTab === 'forecast' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
          {/* Revenue Forecast Chart */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              Revenue Forecast Comparison
            </h3>
            <div style={{ height: '300px', display: 'flex', alignItems: 'end', gap: '1rem', padding: '1rem 0' }}>
              {optimizationData.forecasting.monthly.map((point, index) => (
                <div key={index} style={{ flex: '1', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', marginBottom: '0.5rem' }}>
                    {/* Optimized bar */}
                    <div style={{ 
                      width: '100%', 
                      backgroundColor: '#10b981',
                      borderRadius: '4px 4px 0 0',
                      height: `${(point.optimized / Math.max(...optimizationData.forecasting.monthly.map(p => p.optimized))) * 200}px`,
                      minHeight: '4px',
                      transition: 'height 0.3s ease'
                    }}></div>
                    {/* Current bar */}
                    <div style={{ 
                      width: '100%', 
                      backgroundColor: '#3b82f6',
                      borderRadius: '0 0 4px 4px',
                      height: `${(point.current / Math.max(...optimizationData.forecasting.monthly.map(p => p.optimized))) * 200}px`,
                      minHeight: '4px',
                      transition: 'height 0.3s ease'
                    }}></div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', textAlign: 'center' }}>
                    {point.month.split(' ')[0]}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '600', textAlign: 'center' }}>
                    +{point.growth.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: '16px', height: '16px', backgroundColor: '#3b82f6', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Current Trajectory</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: '16px', height: '16px', backgroundColor: '#10b981', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Optimized Revenue</span>
              </div>
            </div>
          </div>

          {/* Scenarios */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              Revenue Scenarios
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {optimizationData.forecasting.scenarios.map((scenario, index) => (
                <div key={index} style={{ 
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.5rem',
                  border: '1px solid #4a5568'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {scenario.name}
                    </h4>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>
                      {scenario.probability}%
                    </span>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.75rem 0', lineHeight: '1.5' }}>
                    {scenario.description}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ 
                      fontSize: '1rem', 
                      fontWeight: 'bold', 
                      color: scenario.revenueImpact > 0 ? '#10b981' : '#ef4444' 
                    }}>
                      {scenario.revenueImpact > 0 ? '+' : ''}${Math.abs(scenario.revenueImpact).toLocaleString()}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {scenario.timeframe}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'action' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {optimizationData.actionPlan.map((phase, index) => (
            <div key={index} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '1px solid #4a5568'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.5rem 0' }}>
                    {phase.phase}
                  </h3>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Duration: {phase.duration}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.25rem' }}>
                    ${phase.expectedRevenue.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Expected Revenue</div>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '1.5rem' 
              }}>
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                    Key Initiatives
                  </h4>
                  <ul style={{ margin: '0', paddingLeft: '1.5rem', color: '#a0aec0' }}>
                    {phase.initiatives.map((initiative, initIndex) => (
                      <li key={initIndex} style={{ marginBottom: '0.5rem', fontSize: '0.875rem', lineHeight: '1.5' }}>
                        {initiative}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                    Required Resources
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {phase.resources.map((resource, resIndex) => (
                      <span key={resIndex} style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.5rem', 
                        backgroundColor: '#3b82f620',
                        color: '#3b82f6',
                        borderRadius: '0.25rem'
                      }}>
                        {resource}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Total Impact Summary */}
          <div style={{ 
            backgroundColor: '#eff6ff', 
            padding: '2rem', 
            borderRadius: '0.75rem',
            border: '2px solid #bfdbfe'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1e40af', marginBottom: '1rem' }}>
              Total Implementation Impact
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e40af' }}>
                  ${optimizationData.actionPlan.reduce((total, phase) => total + phase.expectedRevenue, 0).toLocaleString()}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>Total Additional Revenue</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e40af' }}>
                  {Math.max(...optimizationData.actionPlan.map(phase => parseInt(phase.duration.split('-')[1])))}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>Weeks to Full Implementation</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e40af' }}>
                  {((optimizationData.actionPlan.reduce((total, phase) => total + phase.expectedRevenue, 0) / optimizationData.overview.totalRevenue) * 100).toFixed(1)}%
                </div>
                <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>Revenue Increase</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}